import { useLayoutEffect, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import {
  CB_IOS,
  CB_OUT_IOS,
  DIM_MAX,
  FADE_MS,
  PARALLAX,
  SETTLE_GUARD_MS,
  SHADOW,
  SLIDE_MS
} from './constants';
import type { Transition } from './types';

export function useDeclarativeTransition(
  transition: Transition | null,
  setTransition: Dispatch<SetStateAction<Transition | null>>,
  locKey: string,
  rootRef: MutableRefObject<HTMLDivElement | null>,
  nodesRef: MutableRefObject<Map<string, HTMLDivElement>>,
  dimRef: MutableRefObject<HTMLDivElement | null>,
  animIdRef: MutableRefObject<number>
) {
  useLayoutEffect(() => {
    if (!transition || transition.leaving.key === locKey) {
      if (transition) setTransition(null);
      return;
    }
    const id = transition.id;
    const slide = transition.mode === 'slide';
    const pop = slide && transition.dir < 0;
    // In a pop the *leaving* (old) screen is on top sliding off; the new screen is
    // revealed underneath. In a push/fade the new screen is on top.
    const topKey = pop ? transition.leaving.key : locKey;
    const underKey = pop ? locKey : transition.leaving.key;
    const top = nodesRef.current.get(topKey) ?? null;
    const under = nodesRef.current.get(underKey) ?? null;
    if (!top || !under) {
      setTransition(null);
      return;
    }
    const dim = dimRef.current;
    const w = rootRef.current?.clientWidth || window.innerWidth;
    const ms = slide ? SLIDE_MS : FADE_MS;
    const curve = slide ? CB_IOS : CB_OUT_IOS;
    animIdRef.current = id;

    // Frame 1 — place both layers at the start of the move with NO transition, then
    // force a reflow so the browser paints them there before we animate.
    top.style.transition = 'none';
    under.style.transition = 'none';
    if (dim) dim.style.transition = 'none';
    top.style.willChange = 'transform, opacity';
    under.style.willChange = 'transform';
    if (slide) {
      top.style.opacity = '1';
      under.style.opacity = '1';
      if (pop) {
        top.style.transform = 'translateX(0px)';
        under.style.transform = `translateX(${-PARALLAX * w}px)`;
        if (dim) dim.style.opacity = String(DIM_MAX);
      } else {
        top.style.transform = `translateX(${w}px)`;
        under.style.transform = 'translateX(0px)';
        if (dim) dim.style.opacity = '0';
      }
    } else {
      top.style.transform = 'none';
      under.style.transform = 'none';
      top.style.opacity = '0';
      under.style.opacity = '1';
      if (dim) dim.style.opacity = '0';
    }
    void top.offsetHeight; // reflow

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      top.removeEventListener('transitionend', onEnd);
      for (const n of [top, under]) {
        n.style.transition = '';
        n.style.transform = '';
        n.style.willChange = '';
        n.style.boxShadow = '';
        n.style.opacity = '';
      }
      if (dim) {
        dim.style.transition = '';
        dim.style.opacity = '';
      }
      if (animIdRef.current === id) setTransition(null);
    };
    const onEnd = (e: TransitionEvent) => {
      if (e.target === top && (e.propertyName === 'transform' || e.propertyName === 'opacity')) {
        finish();
      }
    };

    // Frame 2 — arm the transition and move to the resting position. The compositor
    // owns this from here, so main-thread work (the new screen settling in) can't
    // stutter it.
    const raf = requestAnimationFrame(() => {
      top.addEventListener('transitionend', onEnd);
      if (slide) {
        top.style.transition = `transform ${ms}ms ${curve}`;
        under.style.transition = `transform ${ms}ms ${curve}`;
        if (dim) dim.style.transition = `opacity ${ms}ms ${curve}`;
        top.style.boxShadow = SHADOW;
        if (pop) {
          top.style.transform = `translateX(${w}px)`;
          under.style.transform = 'translateX(0px)';
          if (dim) dim.style.opacity = '0';
        } else {
          top.style.transform = 'translateX(0px)';
          under.style.transform = `translateX(${-PARALLAX * w}px)`;
          if (dim) dim.style.opacity = String(DIM_MAX);
        }
      } else {
        top.style.transition = `opacity ${ms}ms ${curve}`;
        top.style.opacity = '1';
      }
    });
    const guard = window.setTimeout(finish, ms + SETTLE_GUARD_MS + 20);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(guard);
      top.removeEventListener('transitionend', onEnd);
    };
  }, [transition, locKey, setTransition, rootRef, nodesRef, dimRef, animIdRef]);
}
